import * as React from 'react'
import { useCallback } from 'react'
import { Theme } from '@mui/material/styles'
import { FieldProps, getIn } from 'formik'

import { TextFieldProps } from '@mui/material/TextField'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { PopperPlacementType } from '@mui/material/Popper'
import { useLocalizationDateFormat } from '@user/features/Localization/useLocalizationDateFormat'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'

type Props = FieldProps & {
  disabled?: boolean
  disablePast?: boolean
  disableFuture?: boolean
  helperText?: string
  fullWidth?: boolean
  margin?: TextFieldProps['margin']
  required?: boolean
  placeholder?: string
  popperPlacement?: PopperPlacementType
  disableTether?: boolean
  clearable?: boolean
  tidalAdditionalStyling?: boolean
}

export function MuiDatePickerFormik(props: Props) {
  const {
    field,
    form: { isSubmitting, errors, touched, setFieldValue, setFieldTouched },
    disabled = isSubmitting,
    margin,
    fullWidth,
    helperText,
    required,
    popperPlacement,
    disableTether = false,
    clearable,
    tidalAdditionalStyling = false,
    ...rest
  } = props

  const errorText = getIn(errors, field.name)
  const error = getIn(touched, field.name) && errorText !== undefined
  const { getFormat } = useLocalizationDateFormat()

  const tidalThemeActive = useTidalThemeActive()
  const handleChange = useCallback(
    (val) => {
      setFieldTouched(field.name, true)
      setFieldValue(field.name, val)
    },
    [field.name, setFieldTouched, setFieldValue],
  )

  return (
    <DatePicker
      {...field}
      {...rest}
      slotProps={{
        actionBar: {
          actions:
            clearable && !required
              ? ['clear', 'cancel', 'accept']
              : ['cancel', 'accept'],
        },
        popper: {
          placement: popperPlacement,
          modifiers: [
            {
              name: 'preventOverflow',
              enabled: true,
              options: {
                altAxis: true,
                altBoundary: true,
                tether: !disableTether,
                rootBoundary: 'document',
                padding: (theme: Theme) => theme.spacing(8),
              },
            },
          ],
        },
        textField: {
          margin: margin,
          fullWidth: fullWidth,
          helperText: !!errorText && error ? errorText : helperText,
          required: required,
          onBlur: field.onBlur,
          name: field.name,
          error: error,
          inputProps: {
            readOnly: true,
            'data-test-id': `date-picker-input-${field.name}`,
            style:
              tidalThemeActive && tidalAdditionalStyling
                ? {
                    paddingTop: '8.5px',
                    paddingBottom: '8.5px',
                    fontSize: '14px',
                  }
                : {},
          },
        },
      }}
      format={getFormat()}
      onChange={handleChange}
      disabled={disabled}
      onError={console.log}
    />
  )
}
